import { computed, ref } from 'vue'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { useI18n } from 'vue-i18n'
import {
  eligibleFreeTrialDaysFromPackage,
  getCurrentOfferingPackages,
} from '@/services/revenuecat'
import { normaliseWebPricingResponse, trialState } from '@/utils/webPricing'

/**
 * One place that answers "what does Premium cost and is there a trial".
 *
 * Web: everything comes from GET /api/billing/pricing, which the owner controls
 * from the Laravel admin portal. Native: the trial length comes from the store
 * product (RevenueCat); amounts are formatted by the purchase screen from the
 * same product. Nothing here has a default price or a default trial length: if
 * a value is not known, callers get null and must show neutral wording.
 */
const webPricing = ref(null)
const status = ref('idle')
const nativeTrialDays = ref({ monthly: null, annual: null })
let webRequest = null
let autoRetried = false
const AUTO_RETRY_MS = 5000

export function isNativeMobilePlatform () {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())
}

const WEB_PRICING_TTL_MS = 60 * 1000
let webLoadedAt = 0
let webInFlight = false

/** A refresh that fails must not leave the previous terms on screen as if they were current. */
function failWebPricing () {
  webPricing.value = null
  status.value = 'error'
  webRequest = null
}

/**
 * Prices or trial days can change at any time and checkout always uses the saved
 * settings, so a cached read is only reused for a minute. `force` bypasses it
 * before opening checkout.
 */
export function loadWebPricing ({ force = false } = {}) {
  const fresh = Date.now() - webLoadedAt < WEB_PRICING_TTL_MS
  if (webRequest && !force && (fresh || webInFlight)) return webRequest
  if (webPricing.value === null) status.value = 'loading'
  webInFlight = true
  const request = axios.get('/api/billing/pricing')
    .then(({ data }) => {
      const pricing = normaliseWebPricingResponse(data)
      if (pricing) {
        webPricing.value = pricing
        status.value = 'ready'
        webLoadedAt = Date.now()
      } else {
        failWebPricing()
      }
    })
    .catch(() => {
      failWebPricing()
      // One quiet second attempt so a brief blip does not leave the paywall on the retry button.
      if (!autoRetried) {
        autoRetried = true
        setTimeout(() => { void loadWebPricing({ force: true }) }, AUTO_RETRY_MS)
      }
    })
    .finally(() => { webInFlight = false })
  webRequest = request
  return request
}

function normaliseDays (days) {
  return Number.isInteger(days) && days >= 0 ? days : null
}

/**
 * Called by the native purchase flow once the store packages are known. Each
 * package carries its own introductory offer, so the trial is kept per plan.
 */
export function setNativeTrialDays ({ monthly = null, annual = null } = {}) {
  nativeTrialDays.value = { monthly: normaliseDays(monthly), annual: normaliseDays(annual) }
}

/**
 * Components that only need the trial wording (no package list) read the shared
 * offerings snapshot. The paywall's own load writes from the same snapshot it
 * displays, and an expired snapshot refresh replaces rather than preserves its
 * trial terms.
 */
function loadNativeTrialDays () {
  return getCurrentOfferingPackages()
    .then(({ monthly, annual, introEligibility }) => {
      setNativeTrialDays({
        monthly: eligibleFreeTrialDaysFromPackage(monthly, introEligibility.monthly) ?? 0,
        annual: eligibleFreeTrialDaysFromPackage(annual, introEligibility.annual) ?? 0,
      })
    })
    .catch(() => { setNativeTrialDays() })
}

/** Latest normalised web pricing, or null when it has not loaded. */
export function currentWebPricing () {
  return webPricing.value
}

/** Trial days for code outside components (error messages). Null when unknown. */
export function currentTrialDays () {
  return isNativeMobilePlatform() ? nativeTrialDays.value.monthly : (webPricing.value?.trial_days ?? null)
}

/**
 * @param {{ plan?: import('vue').Ref<'monthly' | 'annual'> }} [options] The plan
 * whose store trial applies on native. Web has one trial for both plans.
 */
export function usePricing ({ plan } = {}) {
  const { t } = useI18n()
  const isNative = isNativeMobilePlatform()

  if (isNative) {
    void loadNativeTrialDays()
  } else {
    void loadWebPricing()
  }

  const trialDays = computed(() => (
    isNative
      ? nativeTrialDays.value[plan?.value === 'annual' ? 'annual' : 'monthly']
      : (webPricing.value?.trial_days ?? null)
  ))
  const trialMode = computed(() => trialState(trialDays.value))
  const hasTrial = computed(() => trialMode.value === 'trial')

  const webLoading = computed(() => !isNative && status.value !== 'error' && webPricing.value === null)
  const webFailed = computed(() => !isNative && status.value === 'error' && webPricing.value === null)

  const trialI18n = computed(() => ({
    trial_days: trialDays.value ?? 0,
    days: trialDays.value ?? 0,
    n: trialDays.value ?? 0,
  }))

  const ctaLabel = computed(() => {
    if (trialMode.value === 'trial') return t('planner.trial_cta_primary', trialI18n.value)
    if (trialMode.value === 'none') return t('planner.cta_unlock_premium')
    return t('planner.cta_see_prices')
  })

  return {
    pricing: webPricing,
    status,
    isNative,
    webLoading,
    webFailed,
    trialDays,
    trialMode,
    hasTrial,
    trialI18n,
    ctaLabel,
    nativeTrialDaysFor: planKey => nativeTrialDays.value[planKey === 'annual' ? 'annual' : 'monthly'],
    retry: () => loadWebPricing({ force: true }),
  }
}
